import * as React from 'react';
import { inject, observer } from 'mobx-react';
import * as styles from "./OrgAssetsAdmin.css";
import { OrgAssetsAdminStore } from './stores';
import OrgAssetsService from '../../services/OrgAssetsService';
import { IAButton } from '../button/Button';
import { IAIcon } from '../icon/Icon';
import ViewPanel from './panels/ViewPanel';
import { IOrgAssetsItem } from '../../interfaces/OrgAssets/IOrgAssetsItem';
import { OrgAssetTypeEnum } from '../../interfaces/OrgAssets/OrgAssetTypeEnum';
import { IAPopup } from '../popup/Popup';
import { IOrgAssetRemoveRequest } from '../../interfaces/OrgAssets/IOrgAssetRemoveRequest';
import { OrgAssetStatusEnum } from '../../interfaces/OrgAssets/OrgAssetStatusEnum';
import { LocalizationService } from '../../services/LocalizationService';
import { IAMessageBar } from '../messageBar/MessageBar';
import { Helper } from '../../Helper';

export interface IOrgAssetsAdminProps {
  orgAssetsAdminStore?: OrgAssetsAdminStore;
}

@inject('orgAssetsAdminStore')
@observer
export default class OrgAssetsAdmin extends React.Component<IOrgAssetsAdminProps> {

  private localizationService: LocalizationService;

  constructor(props) {
    super(props);
    this.props.orgAssetsAdminStore.getOrgAssets();
    this.localizationService = new LocalizationService();
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());
  }

  public componentDidMount(): void {
  }

  private onClickAdd(): void {
    const item: IOrgAssetsItem = {
      id: 0,
      displayName: "",
      libraryUrl: {
        decodedUrl: ""
      },
      thumbnailUrl: {
        decodedUrl: ""
      },
      orgAssetType: OrgAssetTypeEnum.ImageDocumentLibrary,
      status: OrgAssetStatusEnum.Adding
    };
    this.props.orgAssetsAdminStore.isAddMode = true;
    this.props.orgAssetsAdminStore.isEditMode = false;
    this.props.orgAssetsAdminStore.isDeleteMode = false;
    this.props.orgAssetsAdminStore.orgAssetSelected = item;
  }

  private onClickEdit(item: IOrgAssetsItem): void {
    this.props.orgAssetsAdminStore.isAddMode = false;
    this.props.orgAssetsAdminStore.isEditMode = true;
    this.props.orgAssetsAdminStore.isDeleteMode = false;
    this.props.orgAssetsAdminStore.orgAssetSelected = item;
    Helper.setLocalStorage("IntraActive_OrgAsset_Selected", JSON.stringify(item));
  }

  private onClickDelete(item: IOrgAssetsItem): void {
    this.props.orgAssetsAdminStore.isAddMode = false;
    this.props.orgAssetsAdminStore.isEditMode = false;
    this.props.orgAssetsAdminStore.isDeleteMode = true;
    this.props.orgAssetsAdminStore.orgAssetSelected = item;
  }

  public render(): JSX.Element {
    let libraries: JSX.Element[] = [];
    let thumbnails: JSX.Element[] = [];
    if (this.props.orgAssetsAdminStore.orgAssets && this.props.orgAssetsAdminStore.orgAssets.orgAssetsLibraries) {
      this.props.orgAssetsAdminStore.orgAssets.orgAssetsLibraries.items.forEach(item => {
        // Localize status enum
        let statusText: string = "";
        switch (item.status) {
          case OrgAssetStatusEnum.Synced:
            statusText = this.localizationService.strings.OrgAssetsAdmin_Status_Synced;
            break;
          case OrgAssetStatusEnum.Adding:
            statusText = this.localizationService.strings.OrgAssetsAdmin_Status_Adding;
            break;
          case OrgAssetStatusEnum.Updating:
            statusText = this.localizationService.strings.OrgAssetsAdmin_Status_Updating;
            break;
          case OrgAssetStatusEnum.Removing:
            statusText = this.localizationService.strings.OrgAssetsAdmin_Status_Removing;
            break;
          default:
            statusText = OrgAssetStatusEnum[item.status];
            break;
        }
        libraries.push(
          <div key={item.displayName} className={styles.columnItem} style={item.status != OrgAssetStatusEnum.Synced ? { pointerEvents: "none", opacity: "0.3" } : {}}>
            <a className={styles.url}
              target="_blank"
              data-interception="off"
              href={item.libraryUrl.decodedUrl}>
              <div style={{ display: "inline-block" }}>
                <IAIcon
                  title={item.orgAssetType == OrgAssetTypeEnum.ImageDocumentLibrary ? "Picture" :
                    item.orgAssetType == OrgAssetTypeEnum.OfficeTemplateLibrary ? "Office" : "File"}
                  size={16}
                  color={"#000000"}
                  style={{
                    display: "inline-block",
                    marginRight: 10
                  }}
                />
                {item.displayName}
              </div>
            </a>
            <div className={styles.columnItemButtons}>
              {item.status != OrgAssetStatusEnum.Synced &&
                <div className={styles.itemStatus}>({statusText})</div>
              }
              <div className={styles.columnItemButton}
                onClick={() => this.onClickEdit(item)}>
                <IAIcon
                  title="Edit"
                  size={18}
                  color={"#333333"}
                />
              </div>
              <div className={styles.columnItemButton}
                onClick={() => this.onClickDelete(item)}>
                <IAIcon
                  title="Trash"
                  size={16}
                  color={"#333333"}
                />
              </div>
            </div>
          </div>
        );
        thumbnails.push(
          <div key={item.displayName} className={styles.columnItem} style={item.status != OrgAssetStatusEnum.Synced ? { pointerEvents: "none", opacity: "0.3" } : {}}>
            <a className={styles.url}
              target="_blank"
              data-interception="off"
              href={item.thumbnailUrl?.decodedUrl ? item.thumbnailUrl.decodedUrl : undefined}>
              {item.thumbnailUrl?.decodedUrl ?
                item.thumbnailUrl.decodedUrl.replace(`https://${this.props.orgAssetsAdminStore.tenant}.sharepoint.com`, '') : this.localizationService.strings.OrgAssetsAdmin_NoImage}
            </a>
          </div>
        );
      });
    }
    return (
      <div className={styles.orgAssetsAdmin}>
        <div className={styles.section}>
          <div className={styles.pageTitle}>{this.localizationService.strings.OrgAssetsAdmin_Title}</div>
          <div className={styles.pageDescription}>{this.localizationService.strings.OrgAssetsAdmin_Description1}</div>
          <div className={`${styles.pageDescription} ${styles.pageDescription2}`}>{this.localizationService.strings.OrgAssetsAdmin_Description2}</div>
          <div className={`${styles.pageDescription} ${styles.pageDescription3}`}>{this.localizationService.strings.OrgAssetsAdmin_Description3}</div>
          <div className={`${styles.pageDescription} ${styles.pageDescription4}`}>{this.localizationService.strings.OrgAssetsAdmin_Description4}</div>
          <div className={`${styles.pageDescription} ${styles.pageDescription5}`}>{this.localizationService.strings.OrgAssetsAdmin_Description5}</div>
        </div>

        {this.props.orgAssetsAdminStore.cdnDisabled && <IAMessageBar
          type="warning"
          onDismiss={() => { }}
          icon="Attention"
          content={<span>{this.localizationService.strings.OrgAssetsAdmin_CdnWarning_1} {" "} <a href="https://learn.microsoft.com/en-us/sharepoint/organization-assets-library#use-microsoft-powershell-to-specify-a-library-as-an-organization-assets-library" target="_blank">{"link"}</a> {" "}{this.localizationService.strings.OrgAssetsAdmin_CdnWarning_2}</span>}
          showIcon={true}
          showCloseButton={false}
          style={{
            clear: "both",
            float: "left",
            borderRadius: 5,
            padding: "0 20px",
            marginBottom: 20
          }}
        />}

        {this.props.orgAssetsAdminStore.orgAssets ?
          <div>
            {this.props.orgAssetsAdminStore.orgAssets.url &&
              <div className={styles.section}>
                <div className={styles.site}>
                  <div className={styles.title}>{this.localizationService.strings.OrgAssetsAdmin_Site}</div>
                  <a className={styles.url}
                    target="_blank"
                    data-interception="off"
                    href={`https://${this.props.orgAssetsAdminStore.tenant}.sharepoint.com${this.props.orgAssetsAdminStore.orgAssets.url.decodedUrl}`}>
                    {this.props.orgAssetsAdminStore.orgAssets.url.decodedUrl}
                  </a>
                </div>
              </div>
            }
            <div className={styles.section}>
              <div className={styles.addButton}
                onClick={() => this.onClickAdd()}>
                <IAIcon
                  title="Add"
                  size={22}
                  color={"#333333"}
                  style={
                    { display: "inline-block" }
                  }
                />
                <div className={styles.addText}>{this.localizationService.strings.OrgAssetsAdmin_Add_AssetsLibrary}</div>
              </div>
            </div>
            {this.props.orgAssetsAdminStore.orgAssets.orgAssetsLibraries &&
              <div className={styles.section}>
                <div>
                  <div className={styles.column1}>
                    <div className={styles.columnTitle}>{this.localizationService.strings.OrgAssetsAdmin_AssetsLibrary}</div>
                    {libraries}
                  </div>
                  <div className={styles.column2}>
                    <div className={styles.columnTitle}>{this.localizationService.strings.OrgAssetsAdmin_ThumbnailImage}</div>
                    {thumbnails}
                  </div>
                </div>
              </div>
            }
          </div>
          :
          <div className={styles.spinner}>
            <IAIcon
              title="Spinner"
              size={20}
              color={"#333333"}
            />
          </div>
        }
        {this.props.orgAssetsAdminStore.isDeleteMode == true && this.props.orgAssetsAdminStore.orgAssetSelected &&
          <IAPopup
            headline={this.localizationService.strings.Messages_DeletePopup_Headline}
            content={
              <div>
                <div
                  style={{
                    float: "left"
                  }}
                >
                  {this.localizationService.strings.Messages_DeletePopup_Message}
                </div>
                <div
                  style={{
                    clear: "both",
                    float: "left"
                  }}
                >
                  <IAButton
                    label={this.localizationService.strings.Messages_DeletePopup_Yes}
                    buttonColor="#02a5e2"
                    showSpinner={this.props.orgAssetsAdminStore.deleting}
                    onClick={async () => {
                      this.props.orgAssetsAdminStore.deleting = true;
                      const item: IOrgAssetRemoveRequest = {
                        libUrl: this.props.orgAssetsAdminStore.orgAssetSelected.libraryUrl
                      };
                      await OrgAssetsService.removeOrgAsset(this.props.orgAssetsAdminStore.environment,
                        this.props.orgAssetsAdminStore.token, this.props.orgAssetsAdminStore.tenant, this.props.orgAssetsAdminStore.orgAssetSelected.id, item);
                      this.props.orgAssetsAdminStore.deleting = false;
                      this.props.orgAssetsAdminStore.isDeleteMode = false;
                      this.props.orgAssetsAdminStore.orgAssetSelected = undefined;
                      await this.props.orgAssetsAdminStore.getOrgAssets();
                    }}
                    style={{
                      marginTop: 20,
                      float: "left"
                    }}
                  />
                  <IAButton
                    label={this.localizationService.strings.Messages_DeletePopup_No}
                    buttonColor={"#999999"}
                    showSpinner={false}
                    onClick={() => {
                      this.props.orgAssetsAdminStore.isDeleteMode = false;
                      this.props.orgAssetsAdminStore.orgAssetSelected = undefined;
                    }}
                    style={{
                      marginTop: 20,
                      marginLeft: 10,
                      float: "left"
                    }}
                  />
                </div>
              </div>
            }
            close={() => {
              this.props.orgAssetsAdminStore.isDeleteMode = false;
              this.props.orgAssetsAdminStore.orgAssetSelected = undefined;
            }}
          />
        }
        <ViewPanel />
      </div>
    );
  }
}